import { CommonModule, DatePipe, Location } from '@angular/common';
import { Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { loadStripe } from '@stripe/stripe-js';
import { of } from 'rxjs';
import { Action } from 'src/app/jobs-shared/job-data/models/action';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { ConfirmationDialogComponent } from 'src/app/shared/dialog/confitmation-dialog/confirmation-dialog.component';
import { CRIMINAL_OFFENSE } from 'src/app/shared/models/criminal-offense.model';
import {
  ROUTE_DQF,
  ROUTE_DQF_BACKGROUND_CHECK_PROCESSING,
  ROUTE_DQF_BIRTH_LOCATION,
  ROUTE_DQF_DOCUMENT_UPLOAD,
  ROUTE_DRIVER_DASHBOARD
} from 'src/app/shared/routes';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import {
  ACTION_TYPE_COLLECT,
  DQF_DOC_STATUS,
  MANAGED_USER_IDS_FOR_WHICH_STRIPE_IS_DISABLED,
  POLICE_CLEARANCE
} from '../constants';
import { DqfDocument } from '../models/dqfDocument';
import { DriverQualificationService } from '../service/driver-qualification.service';

const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const ACTION_ADD_CRIMINAL_CONVICTIONS = 'Add criminal convictions';
const ADDED_RECORD = 'Successfully added record';
const REGX_DESCRIPTION = /^([a-zA-z-,&0-9]{1,60})/;
const DOCUMENT_TYPE = 'Criminal Record (Background) Check';
const BACKGROUND_PROCESSING_INFO = 'Criminal Record Check';
const LOADING_PAYMENT = 'Loading payment gateway...';
const REQUEST_SUCCESS = 'Document request successfull';
const POLICE_CLEARANCE_CERTIFICATE = 'PoliceClearanceCertificate';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY'
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-dqf-criminal-convictions',
  templateUrl: './dqf-criminal-convictions.component.html',
  styleUrls: ['./dqf-criminal-convictions.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DatePipe
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    MatCheckboxModule,
    MatDialogModule
  ]
})
export class DqfCriminalConvictionsComponent implements OnInit, OnDestroy {
  maxDate = new Date();
  criminalOffense = CRIMINAL_OFFENSE;
  criminalConvictionForm: UntypedFormGroup;
  showConvictionToAdd = false;
  requiredMessage = REQUIRED_FIELD_MESSAGE;
  convictionsData = [];
  showAddedConvictions = false;
  editRecordIndex = -1;
  errorMessage: string;
  noConvictions = null;
  loading = false;
  @ViewChild('courtLocation', { static: false }) courtLocation: any;
  publicKey: any = '';
  dqf: any;
  savedPaymentOrders: any;
  driver: any;
  isStripeSessionActive = false;
  isCheckoutMounted: any;
  @ViewChild('checkoutContainer', { static: false }) checkoutRef!: ElementRef<HTMLDivElement>;
  checkout = null;
  isManagedUserDriver = false;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly location: Location,
    private readonly dqfService: DriverQualificationService,
    private readonly zone: NgZone,
    private readonly driverApiService: DriverApiService,
    private readonly dateTimeService: DateTimeService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private registrationService: RegistrationService,
    private readonly datePipe: DatePipe,
    @Inject('environmentData') private environment: any
  ) { }

  ngOnInit(): void {
    this.initializeDqfId();

    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    
    if (!orderPccOutsideofDQF) {
      this.getDqf();
      this.convictionsData = this.dqfService.driverBirthLocationData?.reportData?.convictions || [];
    } else {
      const reportDataStr = sessionStorage.getItem('pccReportData');
      if (reportDataStr) {
        const reportData = JSON.parse(reportDataStr);
        this.convictionsData = reportData?.convictions || [];
      } else {
        this.convictionsData = [];
      }
    }
    
    this.criminalConvictionForm = this.fb.group({
      convictionOffense: new UntypedFormControl('', Validators.required),
      dateOfSentence: new UntypedFormControl('', Validators.required),
      courtLocation: new UntypedFormControl('', Validators.required),
      description: new UntypedFormControl('', [Validators.required, Validators.pattern(REGX_DESCRIPTION)])
    });
    
    this.driver = this.registrationService.getDriver();
    this.savedPaymentOrders = JSON.parse(localStorage.getItem('stripePaymentOrders') || '[]');
    
    const returnRoute = sessionStorage.getItem('postStripeReturnRoute');
    if (returnRoute) {
      setTimeout(() => {
        this.navigateBackToDqfDocuments();
      }, 200);
    }
    const returnRouteOutsideDQF = sessionStorage.getItem('postStripeReturnRouteOutsideDqf');
    if (returnRouteOutsideDQF) {
      setTimeout(() => {
        this.router.navigate([`${ROUTE_DRIVER_DASHBOARD}`]);
      }, 200);
    }
  }

  ngOnDestroy(): void {
    this.safeUnmountCheckout();
  }

  private initializeDqfId(): void {
    if (!this.dqfService.dqfId) {
      // Try from existing dqfData first
      if (this.dqfService.dqfData?.id) {
        this.dqfService.dqfId = this.dqfService.dqfData.id;
      } else {
        // Try from session storage
        const storedDqfId = sessionStorage.getItem('currentDqfId');
        if (storedDqfId) {
          this.dqfService.dqfId = storedDqfId;
        }
      }
    }

    // Always store the current dqfId for the next component
    if (this.dqfService.dqfId) {
      sessionStorage.setItem('currentDqfId', this.dqfService.dqfId);
    }
  }

  getDqf(): void {
    this.loading = true;
    const dqfId = this.dqfService.dqfId;
    this.dqfService.getDqfById(dqfId).subscribe((response: DqfDocument) => {
      if (response) {
        this.dqf = response;
        if (MANAGED_USER_IDS_FOR_WHICH_STRIPE_IS_DISABLED.includes(this.dqf?.managedUserCompanyId)) {
          this.isManagedUserDriver = true;
        }
      }
      this.loading = false;
    });
  }
  onChange(value): void {
    if (value === 'yes') {
      this.showConvictionToAdd = true;
      this.noConvictions = null;
    } else {
      this.noConvictions = value;
      this.showConvictionToAdd = false;
    }
  }

  getLocation() {
    this.getPlaceAutocomplete(this.courtLocation);
  }

  addConviction(isAddMore: boolean): void {
    if (isAddMore) {
      this.criminalConvictionForm.reset();
      this.showAddedConvictions = !this.showAddedConvictions;
      this.showConvictionToAdd = true;
    } else {
      if (this.criminalConvictionForm.valid) {
        const data = {
          offense: this.criminalConvictionForm.value.convictionOffense,
          sentenceDate: this.formatDateWithoutTimezone(this.criminalConvictionForm.value.dateOfSentence),
          courtLocation: this.criminalConvictionForm.value.courtLocation,
          description: this.criminalConvictionForm.value.description
        };
        
        const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
        
        if (this.editRecordIndex >= 0) {
          // Update existing record
          if (!orderPccOutsideofDQF) {
            // DQF flow
            if (this.dqfService.driverBirthLocationData?.reportData?.convictions) {
              this.dqfService.driverBirthLocationData.reportData.convictions[this.editRecordIndex] = data;
              this.convictionsData = this.dqfService.driverBirthLocationData.reportData.convictions;
            }
          } else {
            // Outside DQF flow
            const reportDataStr = sessionStorage.getItem('pccReportData');
            if (reportDataStr) {
              const reportData = JSON.parse(reportDataStr);
              if (reportData?.convictions) {
                reportData.convictions[this.editRecordIndex] = data;
                this.convictionsData = reportData.convictions;
                sessionStorage.setItem('pccReportData', JSON.stringify(reportData));
              }
            }
          }
          this.editRecordIndex = -1;
        } else {
          // Add new record
          if (!orderPccOutsideofDQF) {
            // DQF flow
            if (this.dqfService.driverBirthLocationData?.reportData?.convictions) {
              this.dqfService.driverBirthLocationData.reportData.convictions.push(data);
              this.convictionsData = this.dqfService.driverBirthLocationData.reportData.convictions;
            }
          } else {
            // Outside DQF flow
            const reportDataStr = sessionStorage.getItem('pccReportData');
            if (reportDataStr) {
              const reportData = JSON.parse(reportDataStr);
              if (reportData?.convictions) {
                reportData.convictions.push(data);
                this.convictionsData = reportData.convictions;
                sessionStorage.setItem('pccReportData', JSON.stringify(reportData));
              }
            }
          }
        }
        
        this.showAddedConvictions = !this.showAddedConvictions;
        this.criminalConvictionForm.reset();
      } else {
        this.criminalConvictionForm.markAllAsTouched();
        return;
      }
    }
  }

  showCompleteJobConfirmation(): void {
    const actions = this.createAcceptDialogActions();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Proceed to order PCC?',
        subMessage: 'This action cannot be undone.',
        message: 'CAD 52.50 plus tax will be charged.',
        actions
      },
      disableClose: true
    });
    const confirmAction = actions[1];
    const cancelAction = actions[0];
    confirmAction.action = async () => this.saveConviction(dialogRef, confirmAction, cancelAction);
    cancelAction.action = () => this.dialogCancelAction(dialogRef, confirmAction, cancelAction);

    dialogRef.afterClosed().subscribe();
  }

  saveConviction(dialogRef, confirmAction, cancelAction): void {
    this.loading = true;
    confirmAction.disabled = true;
    cancelAction.disabled = true;

    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');

    // For DQF flow, ensure we have a valid dqfId
    if (!orderPccOutsideofDQF && !this.dqfService.dqfId) {
      this.toastService.showError('Unable to process request. Please try again.');
      this.loading = false;
      confirmAction.disabled = false;
      cancelAction.disabled = false;
      dialogRef.close();
      return;
    }
    
    // Prepare report data based on flow type
    let reportData;
    if (!orderPccOutsideofDQF) {
      // DQF flow - get data from service
      reportData = this.dqfService.driverBirthLocationData?.reportData;
    } else {
      // Outside DQF flow - get data from session storage
      const pccReportDataStr = sessionStorage.getItem('pccReportData');
      if (pccReportDataStr) {
        reportData = JSON.parse(pccReportDataStr);
      }
    }

    const model = {
      attachedDocument: {
        type: POLICE_CLEARANCE,
        status: DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED,
        action: ACTION_TYPE_COLLECT,
        reportData: reportData,
        uploadType: 'request'
      }
    };
    if (!orderPccOutsideofDQF) {
      this.dqfService.updateDqfStatus(this.dqfService.dqfId, model).subscribe(
        () => {
          if (model.attachedDocument.reportData?.convictions?.length) {
            this.toastService.showSuccess(ADDED_RECORD);
          } else {
            if (this.isManagedUserDriver) {
              this.toastService.showSuccess(REQUEST_SUCCESS);
            } else {
              this.toastService.showInfo(LOADING_PAYMENT);
            }
            dialogRef.close();
          }
          this.loading = false;
          dialogRef.close();
          
          if (this.environment?.featureFlags?.enableStripe) {
            const hasPendingOrder = this.savedPaymentOrders.some(order =>
              ['awaitingFulfillment', 'created'].includes(order.status)
            );

            const allFulfilled =
              this.savedPaymentOrders.length > 0 &&
              this.savedPaymentOrders.every(order => order.status === 'fulfilled');

            const noOrdersExist = !this.savedPaymentOrders || this.savedPaymentOrders.length === 0;
            
            if (MANAGED_USER_IDS_FOR_WHICH_STRIPE_IS_DISABLED.includes(this.dqf?.managedUserCompanyId)) {
              this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BACKGROUND_CHECK_PROCESSING}`], {
                queryParams: { documentType: DOCUMENT_TYPE }
              });
            } else {
              this.initiatePayment();
            }
          } else {
            this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BACKGROUND_CHECK_PROCESSING}`], {
              queryParams: { documentType: DOCUMENT_TYPE }
            });
          }
        },
        error => {
          this.loading = false;
          dialogRef.close();
          return of(false);
        }
      );
    } else {
      dialogRef.close();
      this.initiatePayment();
    }
  }

  initiatePayment() {
    this.safeUnmountCheckout();

    setTimeout(async () => {
      await this.renderStripeCheckout();
    }, 500);
  }

  async renderStripeCheckout(): Promise<void> {
    this.loading = true;
    this.isStripeSessionActive = true;
    try {
      const keyResponse = await this.dqfService.getStripePublishableKey().toPromise();
      this.publicKey = keyResponse.publicKey;
      const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
      const noPaymentNeededIds = keyResponse.noPaymentNeeded || [];
      
      if (!orderPccOutsideofDQF) {
        if (noPaymentNeededIds.includes(this.dqf.managedUserCompanyId)) {
          this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BACKGROUND_CHECK_PROCESSING}`], {
            queryParams: { documentType: DOCUMENT_TYPE }
          });
          this.isStripeSessionActive = false;
          return;
        }
      }

      const stripe = await loadStripe(this.publicKey);
      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }
      
      const origin = window.location.origin;
      const successReturnUrl = `${origin}/#/driverQualification/redirect-stripe?session_id={CHECKOUT_SESSION_ID}`;

      const sessionResponse = await this.dqfService
        .createStripePaymentSession({
          successReturnUrl: successReturnUrl,
          product: POLICE_CLEARANCE_CERTIFICATE,
          userId: this.driver.id,
          customerEmail: this.driver.email
        })
        .toPromise();
      
      sessionStorage.setItem('stripeCheckoutSessionResponse', JSON.stringify(sessionResponse));
      if (orderPccOutsideofDQF) {
        sessionStorage.setItem('postStripeReturnRouteOutsideDqf', '/paymentStatus');
      } else {
        sessionStorage.setItem('postStripeReturnRoute', '/paymentStatus');
      }
      
      const clientSecret = sessionResponse.paymentSessionKey;
      
      this.checkout?.unmount();
      this.checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: async () => clientSecret
      });
      
      setTimeout(() => {
        if (this.checkout && this.checkoutRef?.nativeElement) {
          this.checkout.mount(this.checkoutRef.nativeElement);
          this.isCheckoutMounted = true;
        }
      }, 0);
      this.isCheckoutMounted = true;
    } catch (error) {
      this.toastService.showError('Payment setup failed. Please try again.');
      this.isStripeSessionActive = false;
    } finally {
      this.loading = false;
    }
  }

  private safeUnmountCheckout(): void {
    try {
      if (this.checkout && this.isCheckoutMounted) {
        this.checkout.unmount();
        this.checkout.destroy();
      }
    } catch (error) {
      return error;
    } finally {
      this.isCheckoutMounted = false;
      this.checkout = null;
    }
    if (this.checkoutRef?.nativeElement) {
      this.checkoutRef.nativeElement.innerHTML = '';
    }
    const container = this.checkoutRef?.nativeElement;
    if (container) {
      container.innerHTML = '';
    }

    this.checkout = null;
    this.isCheckoutMounted = false;
  }

  onCancel(): void {
    this.editRecordIndex = -1;
  }

  onUpdateConviction(data: any, index: number) {
    this.showAddedConvictions = !this.showAddedConvictions;
    this.showConvictionToAdd = true;
    
    // Convert date string back to Date object for the form
    const sentenceDate = data.sentenceDate ? new Date(data.sentenceDate) : null;
    
    this.criminalConvictionForm.setValue({
      convictionOffense: data.offense,
      dateOfSentence: sentenceDate,
      courtLocation: data.courtLocation,
      description: data.description
    });
    this.editRecordIndex = index;
  }

  onDeleteConviction(index: number) {
    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    
    if (!orderPccOutsideofDQF) {
      // DQF flow
      if (this.dqfService.driverBirthLocationData?.reportData?.convictions) {
        this.dqfService.driverBirthLocationData.reportData.convictions.splice(index, 1);
        this.convictionsData = this.dqfService.driverBirthLocationData.reportData.convictions;
      }
    } else {
      // Outside DQF flow
      const reportDataStr = sessionStorage.getItem('pccReportData');
      if (reportDataStr) {
        const reportData = JSON.parse(reportDataStr);
        if (reportData?.convictions) {
          reportData.convictions.splice(index, 1);
          this.convictionsData = reportData.convictions;
          sessionStorage.setItem('pccReportData', JSON.stringify(reportData));
        }
      }
    }
    
    // Reset edit mode if we're deleting the record being edited
    if (this.editRecordIndex === index) {
      this.editRecordIndex = -1;
    } else if (this.editRecordIndex > index) {
      // Adjust edit index if we deleted a record before the one being edited
      this.editRecordIndex--;
    }
  }

  navigateBack(): void {
    this.safeUnmountCheckout();
    this.isStripeSessionActive = false;
    
    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    
    setTimeout(() => {
      if (!orderPccOutsideofDQF) {
        // DQF flow - go back to DQF documents
        this.navigateBackToDqfDocuments();
      } else {
        // Outside DQF flow - go back to birth location page
        this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BIRTH_LOCATION}`]);
      }
    }, 200);
  }

  private navigateBackToDqfDocuments(): void {
    const dqfId = this.dqfService.dqfId || sessionStorage.getItem('currentDqfId');
    if (dqfId) {
      this.router.navigate([ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD, dqfId]);
    } else {
      this.router.navigate([ROUTE_DQF]);
    }
  }

  cancelConviction() {
    // Reset edit state
    this.editRecordIndex = -1;
    this.criminalConvictionForm.reset();
    
    if (this.convictionsData?.length) {
      this.showAddedConvictions = !this.showAddedConvictions;
      this.showConvictionToAdd = false;
    } else {
      this.noConvictions = null;
      this.showConvictionToAdd = false;
    }
  }

  private getPlaceAutocomplete(courtLocation: any): void {
    if (!courtLocation?.nativeElement) {
      return;
    }

    const input = courtLocation.nativeElement;

    // Clean up existing autocomplete instance if it exists
    if (input.autocompleteInstance) {
      google.maps.event.clearInstanceListeners(input);
      input.autocompleteInstance = null;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(courtLocation.nativeElement, {
        types: ['(regions)'],
        // remove  componentRestrictions for country if we have more than 5 countries
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Set fields explicitly to reduce payload and improve mobile performance
      autocomplete.setFields(['formatted_address', 'address_components', 'geometry']);

      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        
        // Check if place has required data
        if (place && place.formatted_address) {
          this.zone.run(() => {
            try {
              this.criminalConvictionForm.patchValue({ courtLocation: place.formatted_address });
            } catch (error) {
              console.error('Error processing place selection:', error);
            }
          });
        }
      });

      // Mobile-specific: Ensure autocomplete works well on mobile
      // Note: Removed focus event trigger to prevent Google Maps API errors

      // Store autocomplete instance in the element for later removal
      input.autocompleteInstance = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }

  private dialogCancelAction(dialogRef: MatDialogRef<any>, confirmAction: Action, cancelAction: Action): any {
    confirmAction.disabled = true;
    cancelAction.disabled = true;

    dialogRef.close();
  }

  private createAcceptDialogActions(): Action[] {
    const actions: Action[] = [];
    const NoAction = {
      name: 'Cancel',
      action: (data: any) => { },
      disabled: false
    } as Action;
    actions.push(NoAction);

    const OkayAction = {
      name: 'Proceed',
      action: (data: any) => { },
      disabled: false,
      color: 'primary'
    } as Action;
    actions.push(OkayAction);

    return actions;
  }

  /**
   * Formats a Date object to 'yyyy-MM-dd' string without timezone conversion.
   * This prevents date shifting issues when the datepicker's date is converted to a string.
   */
  private formatDateWithoutTimezone(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
}
